import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import PDFViewer from './PDFViewer';
import JSONViewer from './JSONViewer';
import AWS from 'aws-sdk';
import { FiHome, FiCheck, FiX, FiFileText } from 'react-icons/fi';
import AccellorLogo from '../assets/accellor_logo.svg';
import config from '../config';
import AccellorLoading from './AccellorLoading';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 30px;
  background: white;
  color: #333;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  img {
    height: 40px;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 500;
  color: #2c3e50;

  .file-icon {
    color: #3498db;
  }

  .document-name {
    color: #7f8c8d;
    font-size: 16px;
    font-weight: normal;
    margin-left: 8px;
  }
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
  }

  ${props => props.variant === 'home' && `
    background: #f5f5f5;
    color: #333;
    &:hover {
      background: #e5e5e5;
    }
  `}

  ${props => props.variant === 'accept' && `
    background: #27ae60;
    color: white;
    &:hover {
      background: #219a52;
    }
  `}

  ${props => props.variant === 'reject' && `
    background: #e74c3c;
    color: white;
    &:hover {
      background: #c0392b;
    }
  `}
`;

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  background: #fff;
`;

const Notification = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 15px 25px;
  border-radius: 4px;
  background: ${props => props.type === 'success' ? '#27ae60' : '#e74c3c'};
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const LoadingMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 100px);
  padding: 20px;
  color: #666;
`;

const ErrorMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #cc0000;
`;

const VerificationPage = () => {
  const { documentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [jsonData, setJsonData] = useState(null);
  const [selectedBox, setSelectedBox] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [notification, setNotification] = useState(null);
  const [documentName, setDocumentName] = useState(location.state?.documentName || null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // If document name is not in state, try to fetch it
        if (!documentName) {
          try {
            const docResponse = await axios.get(`${config.API_BASE_URL}/document/${documentId}`);
            if (docResponse.data?.document_name) {
              setDocumentName(docResponse.data.document_name);
            } else {
              throw new Error('Document name not found in response');
            }
          } catch (err) {
            console.error('Error fetching document name:', err);
            throw new Error('Unable to retrieve document name. Please try again from the dashboard.');
          }
        }

        // Get the JSON results
        const resultsResponse = await axios.get(`${config.API_BASE_URL}/get-results/${documentId}`);
        
        // Add proper error handling for merged_content
        if (!resultsResponse.data || !resultsResponse.data.merged_content) {
          throw new Error('No data available for this document yet. Please try again later.');
        }
        
        setJsonData(resultsResponse.data.merged_content);

        // Get presigned URL for PDF download from backend
        const presignedResponse = await axios.get(`${config.API_BASE_URL}/get-presigned-download-url`, {
          params: {
            filename: documentName
          }
        });

        if (presignedResponse.data?.url) {
          setPdfUrl(presignedResponse.data.url);
        } else if (presignedResponse.data?.error) {
          throw new Error(presignedResponse.data.error);
        } else {
          throw new Error('Failed to get PDF download URL');
        }

      } catch (err) {
        console.error('Error:', err);
        setError(err.message || 'Failed to fetch document data');
      } finally {
        setLoading(false);
      }
    };

    if (documentId) {
      fetchData();
    } else {
      setError('No document ID provided');
      setLoading(false);
    }
  }, [documentId, documentName]);

  const handleReferenceClick = (coordinates, pageNumber, label) => {
    setSelectedBox({
      bbox: coordinates,
      pageNumber: pageNumber,
      label
    });
  };

  const handleVerification = async (status) => {
    try {
      await axios.post(`${config.API_BASE_URL}/update-verification-status`, {
        document_id: documentId,
        status: status
      });
      setNotification({
        type: 'success',
        message: `Document ${status === 'accept' ? 'accepted' : 'rejected'} successfully`
      });
      
      // Clear notification after 3 seconds
      setTimeout(() => {
        setNotification(null);
        navigate('/');
      }, 3000);
    } catch (err) {
      setNotification({
        type: 'error',
        message: 'Failed to update verification status'
      });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  if (loading) {
    return (
      <LoadingMessage>
        <AccellorLoading size={100} />
      </LoadingMessage>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Header>
          <HeaderLeft>
            <img 
              src={AccellorLogo} 
              alt="Accellor Logo" 
              onClick={() => navigate('/')}
              title="Go to Dashboard"
            />
          </HeaderLeft>
        </Header>
        <ErrorMessage>
          {error}
          <Button variant="home" onClick={() => navigate('/')} style={{ marginTop: '1rem' }}>
            <FiHome size={18} />
            Return to Dashboard
          </Button>
        </ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <HeaderLeft>
          <img 
            src={AccellorLogo} 
            alt="Accellor Logo" 
            onClick={() => navigate('/')}
            title="Go to Dashboard"
          />
          <HeaderTitle>
            <FiFileText className="file-icon" size={24} />
            Response Validation
            <span className="document-name">{documentName}</span>
          </HeaderTitle>
        </HeaderLeft>
        <HeaderButtons>
          <Button variant="home" onClick={() => navigate('/')}>
            <FiHome size={18} />
            Home
          </Button>
          <Button variant="accept" onClick={() => handleVerification('accept')}>
            <FiCheck size={18} />
            Accept
          </Button>
          <Button variant="reject" onClick={() => handleVerification('reject')}>
            <FiX size={18} />
            Reject
          </Button>
        </HeaderButtons>
      </Header>

      <ContentContainer>
        <JSONViewer 
          data={jsonData}
          onReferenceClick={handleReferenceClick}
          isProcessing={loading}
        />
        <PDFViewer 
          file={pdfUrl}
          selectedBox={selectedBox}
        />
      </ContentContainer>

      {notification && (
        <Notification type={notification.type}>
          {notification.message}
        </Notification>
      )}
    </PageContainer>
  );
};

export default VerificationPage;